import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Input, Popover, Select } from "antd";
import { totalWellsCountAtom } from "atoms";
import {
  BUBBLE_LAYER,
  BUBBLE_MIN_ZOOM,
  COMPLETION_MIN_ZOOM,
  COVERAGE_LAYER,
  COVERAGE_MIN_ZOOM,
  XDA_PROJECTION_MIN_ZOOM
} from "constants/mapLayers.constants";
import { scaleByOptions } from "constants/visualization.constants";
import { useAtomValue } from "jotai";
import _debounce from "lodash/debounce";
import styled from "styled-components";
import { UnitSystem } from "types/units";

import {
  useEvaUnitCore,
  useFeatureFlags,
  useRadiusConverter,
  useUnitLabel,
  useUserUnit
} from "hooks";

import { getCompletion, getExtrusion } from "api/map";

import { PdenSourceEnum } from "models/pdenDataSourceSetting";

import { BaseDropdown, BaseInput, LabeledSwitch, ToolbarButton } from "components/base";
import { useGeomBinContext } from "components/geom-bin/hooks/useGeomBinContext";
import { Bubble } from "components/icons";

import { TxnT } from "../../../models";
import useGetCoverage from "../hooks/useGetCoverage";
import { useMapContext } from "../hooks/useMapContext";
import { useMapDispatch } from "../hooks/useMapDispatch";
import useXdaProjectionLayer, {
  XdaProjectionLayerProps
} from "../hooks/useXdaProjectionLayer";
import { WellBubbleVis } from "./WellBubbleVis";
import XdaProjectionOptions from "./XdaProjectionOptions";

export interface MapVisComponentModel {
  mapbox: mapboxgl.Map;
  filterId;
  txnId;
  removeLayer;
  addLayer;
}

export type CoverageType = "Bubble" | "WellOutline";

// Coverage geojson has already been fetched by the time this runs (the request
// itself is gated in useGetCoverage), so this only coalesces repaints and costs
// no extra requests. Until it elapses the coverage layer still shows the
// previous transaction's colours, which is how long wells and coverage can
// disagree after a bin colour change.
export const COVERAGE_REPAINT_DEBOUNCE_MS = 500;

export enum VisualizationOption {
  Bubble = "showBubble",
  Completion = "showCompletion",
  Coverage = "showCoverage",
  XdaProjection = "showXdaProjection"
}

export type BubbleOptionsT = {
  visible: boolean;
  maxRadiusSizeMeters: number;
  location: "midpoint" | "bottom" | "surface";
  opacity: number;
  visualType?: "showBubble" | "showExtrude";
};

export default function MapVis({
  mapbox,
  filterId,
  txnId,
  removeLayer,
  addLayer
}: MapVisComponentModel) {
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);

  const [visualizationOption, setVisualizationOption] =
    useState<Maybe<VisualizationOption>>(null);
  const [bubbleExtrudeField, setBubbleExtrudeField] = useState<
    { property: string; title: string; pdenSource?: PdenSourceEnum }[]
  >([]);
  //bubble field
  const [canShowBubble, setCanShowBubble] = useState(true);
  const [, setShowBubbleExtrudeFields] = useState(false);
  const [scaleByProperty, setScaleByProperty] = useState(scaleByOptions[0]);
  const [scaleByValue, setScaleByValue] = useState(scaleByOptions[0].defaultValue);
  const [displayedScaleByValue, setDisplayedScaleByValue] = useState<string>(
    scaleByOptions[0].defaultValue.toString()
  );
  const [isEditingScaleByValue, setIsEditingScaleByValue] = useState(false);
  const { hasFeature } = useFeatureFlags();
  const hasBubbleVisual = hasFeature("Map Bubble Visual");
  const hasCompletionVisual = hasFeature("Map Completion Visual");
  const hasWellCoverageVisual = hasFeature("Map Well Coverage Visual");
  const hasXdaOverlapVisual = hasFeature("Map XDA Overlap Visual");
  const hasAnyMapVisual =
    hasBubbleVisual ||
    hasCompletionVisual ||
    hasWellCoverageVisual ||
    hasXdaOverlapVisual;
  const mapDispatch = useMapDispatch();
  const { baseLayer } = useMapContext();
  const { convertByUnit, getTargetUnit, isReady: isUnitCoreReady } = useEvaUnitCore();
  const unitType = useUserUnit();
  const [completionLengthValue, setCompletionLengthValue] = useState(500);
  const { displayLabel, metersToDisplay, displayToMeters, isReady } =
    useRadiusConverter();
  const { getLabel: getUnitLabel } = useUnitLabel();

  const [displayedCompletionLength, setDisplayedCompletionLength] = useState<string>(
    metersToDisplay(500).toString()
  );
  const [isEditingCompletionLength, setIsEditingCompletionLength] = useState(false);
  const [displayedCoverageRadius, setDisplayedCoverageRadius] = useState<string>(
    metersToDisplay(600).toString()
  );
  const [isEditingCoverageRadius, setIsEditingCoverageRadius] = useState(false);
  const [bubbleOptions, setBubbleOptions] = useState<BubbleOptionsT>({
    maxRadiusSizeMeters: 200,
    location: "midpoint",
    opacity: 100,
    visible: true,
    visualType: "showBubble"
  });
  const [showCompletion, setShowCompletion] = useState(false);
  const [canToggleCompletion, setCanToggleCompletion] = useState(true);
  const [canToggleWellCoverage, setCanToggleWellCoverage] = useState(false);
  const [isZoomSufficient, setIsZoomSufficient] = useState(false);
  const totalWellsCount = useAtomValue(totalWellsCountAtom);
  const XDA_PROJECTION_MAX_WELLS = 1000;
  const canToggleXdaProjection =
    isZoomSufficient && totalWellsCount < XDA_PROJECTION_MAX_WELLS;

  const xdaProjectionTooltip = useMemo(() => {
    if (!isZoomSufficient) {
      return "Zoom in on the map to enable this visualization.";
    }
    if (totalWellsCount >= XDA_PROJECTION_MAX_WELLS) {
      return `XDA Projection is only available when the well count is under ${XDA_PROJECTION_MAX_WELLS}. Current count: ${totalWellsCount}.`;
    }
    return "";
  }, [isZoomSufficient, totalWellsCount]);

  const { isActive: isGeomBinOpen } = useGeomBinContext();
  const [coverageOptions, setCoverageOptions] = useState({
    showCoverage: false,
    radius: 600,
    coverageType: "Bubble" as CoverageType
  });

  const { data: coverageData } = useGetCoverage(coverageOptions);

  const isXdaProjectionActive = visualizationOption === VisualizationOption.XdaProjection;

  useEffect(() => {
    if (!scaleByProperty || !isUnitCoreReady || isEditingScaleByValue) {
      return;
    }
    const siValue = scaleByProperty.defaultValue;
    setScaleByValue(siValue);
    const converted = convertByUnit(scaleByProperty.unit, unitType, siValue, 2);
    setDisplayedScaleByValue(converted.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleByProperty, isUnitCoreReady]);

  useEffect(() => {
    if (isReady && !isEditingCompletionLength) {
      const convertedValue = metersToDisplay(completionLengthValue);
      const roundedValue = Math.round(convertedValue * 100) / 100;
      setDisplayedCompletionLength(roundedValue.toString());
    }
  }, [completionLengthValue, isReady, metersToDisplay, isEditingCompletionLength]);

  useEffect(() => {
    if (isReady && !isEditingCoverageRadius) {
      const convertedValue = metersToDisplay(coverageOptions.radius);
      const roundedValue = Math.round(convertedValue * 100) / 100;
      setDisplayedCoverageRadius(roundedValue.toString());
    }
  }, [coverageOptions.radius, isReady, metersToDisplay, isEditingCoverageRadius]);

  useEffect(() => {
    if (!mapbox) {
      return;
    }

    function updateZoomState() {
      if (!mapbox) {
        return;
      }
      const zoom = mapbox.getZoom();
      setCanShowBubble(zoom > BUBBLE_MIN_ZOOM);
      setCanToggleCompletion(zoom > COMPLETION_MIN_ZOOM);
      setCanToggleWellCoverage(zoom > COVERAGE_MIN_ZOOM);
      setIsZoomSufficient(zoom > XDA_PROJECTION_MIN_ZOOM);
    }

    // Check initial zoom level on mount
    updateZoomState();

    // Listen to both zoom and zoomend for responsive UI updates
    mapbox.on("zoom", updateZoomState);
    mapbox.on("zoomend", updateZoomState);
    return () => {
      if (mapbox) {
        mapbox.off("zoom", updateZoomState);
        mapbox.off("zoomend", updateZoomState);
      }
    };
  }, [mapbox]);

  //bubble map layer
  useEffect(() => {
    if (!mapbox) {
      return;
    }

    if (!bubbleOptions.visualType || bubbleExtrudeField.length == 0 || !txnId) {
      removeLayer(BUBBLE_LAYER);
      if (mapbox.getSource(BUBBLE_LAYER)) {
        mapbox.removeSource(BUBBLE_LAYER);
      }
      return;
    }

    mapDispatch({
      payload: {
        isLoading: true
      }
    });
    if (
      bubbleOptions.visualType &&
      bubbleExtrudeField.length > 0 &&
      bubbleExtrudeField[0].property
    ) {
      getExtrusionDebounce(
        mapbox,
        txnId,
        bubbleExtrudeField,
        bubbleOptions,
        visualizationOption
      );
    }
  }, [
    bubbleExtrudeField,
    txnId,
    removeLayer,
    bubbleOptions,
    visualizationOption,
    baseLayer
  ]);

  const getExtrusionDebounce = useRef(
    _debounce(
      (
        mapbox,
        txnId: TxnT,
        bubbleExtrudeField: {
          property: string;
          title: string;
          unit?: string;
          product?: string;
          pdenSource?: number;
        }[],
        options: BubbleOptionsT,
        visualizationOption: Maybe<VisualizationOption>
      ) => {
        getExtrusion(
          txnId,
          options.visualType === "showBubble",
          bubbleExtrudeField,
          options.visualType === "showExtrude",
          options
        )
          .then((response) => {
            const data = response.data;
            mapDispatch({
              payload: {
                isLoading: false
              }
            });
            try {
              removeLayer(BUBBLE_LAYER);
              if (mapbox.getSource(BUBBLE_LAYER)) {
                mapbox.removeSource(BUBBLE_LAYER);
              }
              if (visualizationOption !== VisualizationOption.Bubble) {
                return;
              }
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error(err);
              //empty
            }
            const fc = { ...emptyFeatureCollection };
            const valueKey =
              options.visualType === "showExtrude" ? "extrude-value" : "bubble-value";
            fc.features = data.data.features.filter((f) => {
              return f.properties[valueKey] > 0;
            });
            const units = [];
            let colors = [];
            const title =
              bubbleExtrudeField.length == 1
                ? bubbleExtrudeField[0].title.replace(/\(.*\)/, "")
                : "Multiple";
            if (data.colors.length > 1) {
              colors = data.colors.map((c, i) => {
                const field = bubbleExtrudeField[i];
                const name = field.title;
                if (field.unit && !units.includes(field.unit)) {
                  units.push(field.unit);
                }
                if (field.property === c.title) {
                  return {
                    title: `${field.product ? field.product + ` ${name}` : `${name}`}`,
                    color: c.color
                  };
                }
                return c;
              });
            } else if (bubbleExtrudeField.length > 0 && bubbleExtrudeField[0].unit) {
              units.push(bubbleExtrudeField[0].unit);
            }

            const hasGasAndOilUnits =
              bubbleExtrudeField.length > 1 &&
              bubbleExtrudeField.findIndex((f) =>
                f.property.startsWith("Performance.Oil")
              ) >= 0 &&
              bubbleExtrudeField.findIndex((f) =>
                f.property.startsWith("Performance.Gas")
              ) >= 0;
            const unitDisplay = hasGasAndOilUnits
              ? "MBOE"
              : units.length > 0
              ? units.join("+")
              : "";
            mapDispatch({
              payload: {
                bubbleVisLegend: {
                  visible:
                    options.visible && visualizationOption === VisualizationOption.Bubble,
                  minValue: data.min,
                  maxValue: data.max,
                  title: unitDisplay ? `${title} (${unitDisplay})` : title,
                  colors
                }
              }
            });
            mapbox.addSource(BUBBLE_LAYER, { type: "geojson", data: fc });
            let paint: unknown = {
              "fill-color": ["get", "color"],
              "fill-outline-color": "#4b4a4a",
              "fill-opacity": options.opacity * 0.01
            };
            if (options.visualType === "showExtrude") {
              paint = {
                "fill-extrusion-color": ["get", "color"],
                "fill-extrusion-height": ["get", "extrude"],
                "fill-extrusion-base": 0,
                "fill-extrusion-opacity": 0.8
              };
            }
            const layer = {
              id: BUBBLE_LAYER,
              type: options.visualType === "showExtrude" ? "fill-extrusion" : "fill",
              source: BUBBLE_LAYER,
              layout: {
                visibility: "visible"
              },
              minzoom: BUBBLE_MIN_ZOOM,
              paint
            };

            addLayer(layer);
          })
          .finally(() => {
            mapDispatch({
              payload: {
                isLoading: false
              }
            });
          });
      },
      2000
    )
  ).current;

  function removeCoverageLayer(mapbox) {
    if (!mapbox) {
      return;
    }
    // Separate try blocks on purpose: mapbox's removeLayer throws against a
    // style that has not finished loading, and sharing one block meant that
    // throw skipped removeSource and left an orphaned source behind, which then
    // made every later addSource fail with "There is already a source with this
    // ID". The source has to be dropped whether or not the layer removal works.
    try {
      removeLayer(COVERAGE_LAYER);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
    try {
      if (mapbox.getSource(COVERAGE_LAYER)) {
        mapbox.removeSource(COVERAGE_LAYER);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }

  const getCompletionDebounceRef = useRef(
    _debounce(
      (source, filterId, scaleByProperty, scaleByValue, completionLengthValue) => {
        mapDispatch({
          payload: {
            isLoading: true
          }
        });
        getCompletion(
          filterId?.id,
          scaleByProperty.field,
          scaleByValue,
          completionLengthValue
        )
          .then((response) => {
            const data = response.data;
            source.setData(data);
          })
          .finally(() => {
            mapDispatch({
              payload: {
                isLoading: false
              }
            });
          });
      },
      500
    )
  ).current;
  useEffect(() => {
    if (visualizationOption === VisualizationOption.Completion) {
      setShowCompletion(true);
      setCoverageOptions({ ...coverageOptions, showCoverage: false });
      removeCoverageLayer(mapbox);
    } else if (visualizationOption === VisualizationOption.Bubble) {
      setShowCompletion(null);
      setCoverageOptions({ ...coverageOptions, showCoverage: false });
      removeCoverageLayer(mapbox);
    } else if (visualizationOption === VisualizationOption.Coverage) {
      setShowCompletion(null);
      setCoverageOptions({ ...coverageOptions, showCoverage: true });
    } else {
      setShowCompletion(null);
      setCoverageOptions({ ...coverageOptions, showCoverage: false });
      removeCoverageLayer(mapbox);
    }
  }, [visualizationOption, mapbox]);

  useEffect(() => {
    function showCompletions() {
      if (!mapbox) {
        return;
      }
      const zoom = mapbox.getZoom();
      const source = mapbox.getSource("completion") as mapboxgl.GeoJSONSource;
      if (zoom < COMPLETION_MIN_ZOOM || !showCompletion) {
        if (source) {
          source.setData(emptyFeatureCollection);
        }
        return;
      }
      getCompletionDebounceRef(
        source,
        filterId,
        scaleByProperty,
        scaleByValue,
        completionLengthValue
      );
    }

    showCompletions();
  }, [
    showCompletion,
    filterId,
    scaleByProperty,
    scaleByValue,
    completionLengthValue,
    getCompletionDebounceRef,
    baseLayer
  ]);

  useEffect(() => {
    if (!coverageOptions.showCoverage || !coverageData || !mapbox) {
      return;
    }

    const debouncedUpdate = _debounce(() => {
      try {
        // Repaint in place whenever the layer is already on the map. setData
        // carries the new colours (paint reads them off each feature via
        // ["get", "color"]) and cannot throw a duplicate-ID error, so a repaint
        // that fails mid-style-load can no longer strand the previous
        // transaction's colours: removeLayer throws before removing anything,
        // which used to skip removeSource and leave every later addSource
        // throwing "There is already a source with this ID" forever. Not
        // re-inserting the layer also keeps its z-order stable across repaints.
        const existingSource = mapbox.getSource(COVERAGE_LAYER) as
          | mapboxgl.GeoJSONSource
          | undefined;
        if (existingSource && mapbox.getLayer(COVERAGE_LAYER)) {
          existingSource.setData(coverageData);
          return;
        }

        // Either nothing is painted yet, or a previous failure left a source
        // without its layer — tear down whatever is left before re-adding.
        removeCoverageLayer(mapbox);

        mapbox.addSource(COVERAGE_LAYER, { type: "geojson", data: coverageData });
        const paint: unknown = {
          "fill-color": ["get", "color"],
          "fill-outline-color": ["get", "color"],
          "fill-opacity": 0.4
        };

        addLayer({
          id: COVERAGE_LAYER,
          type: "fill",
          source: COVERAGE_LAYER,
          layout: {
            visibility: "visible"
          },
          minzoom: COVERAGE_MIN_ZOOM,
          paint
        });
      } catch (error) {
        // Reached only on the add path: addSource throws against a style that
        // has not finished loading, which a base-map switch can produce.
        // Previously that escaped the debounce timer as an uncaught error with
        // nothing recorded. The layer is still missing until the next repaint,
        // but that repaint now recovers on its own rather than failing on a
        // leftover source. See the PR for why restoring it automatically after
        // a base-map switch is deferred to its own ticket.
        Sentry.withScope((scope) => {
          scope.setContext("map_coverage_repaint", {
            coverageType: coverageOptions.coverageType,
            radius: coverageOptions.radius
          });
          scope.setLevel("error");
          Sentry.captureException(error);
        });
      }
    }, COVERAGE_REPAINT_DEBOUNCE_MS);

    debouncedUpdate();

    return () => {
      debouncedUpdate.cancel();
    };
  }, [coverageData, coverageOptions.showCoverage, baseLayer]);

  if (isGeomBinOpen || !hasAnyMapVisual) {
    return <></>;
  }

  return (
    <>
      {isXdaProjectionActive && (
        <XdaProjectionLayerEffect
          txnId={txnId?.id ?? null}
          canFetch={canToggleXdaProjection}
          removeLayer={removeLayer}
          addLayer={addLayer}
        />
      )}
      <Popover
        arrowPointAtCenter
        open={isPopoverVisible}
        placement="rightTop"
        trigger="click"
        content={
          <div className="ipdb-options">
            {hasBubbleVisual && (
              <>
                <MapVisOptionItem className="ipdb-option-item">
                  <LabeledSwitch
                    testId="map-vis_show-bubble"
                    switch={{
                      showSwitchTooltip: !canShowBubble,
                      isDisabled: !canShowBubble,
                      isChecked:
                        visualizationOption === VisualizationOption.Bubble &&
                        canShowBubble,
                      onChange: (val) => {
                        setVisualizationOption(val ? VisualizationOption.Bubble : null);
                        mapDispatch({
                          payload: {
                            bubbleVisLegend: {
                              visible: val && bubbleExtrudeField.length > 0,
                              title: "",
                              maxValue: 0,
                              minValue: 0,
                              colors: []
                            }
                          }
                        });
                      },
                      size: "large"
                    }}
                    label={{
                      value: "Show Bubble",
                      isBold: true
                    }}
                  />
                </MapVisOptionItem>

                <div className="separator" />

                {visualizationOption === VisualizationOption.Bubble && (
                  <WellBubbleVis
                    bubbleExtrudeField={bubbleExtrudeField}
                    setBubbleExtrudeField={setBubbleExtrudeField}
                    onChange={(opts) => {
                      setBubbleOptions(opts);
                    }}
                    setShowBubbleExtrudeFields={setShowBubbleExtrudeFields}
                    options={bubbleOptions}
                  />
                )}
              </>
            )}

            {hasCompletionVisual && (
              <>
                <MapVisOptionItem className="ipdb-option-item">
                  <LabeledSwitch
                    testId="map-vis_show-completion"
                    switch={{
                      showSwitchTooltip: !canToggleCompletion,
                      isDisabled: !canToggleCompletion,
                      isChecked:
                        visualizationOption === VisualizationOption.Completion &&
                        canToggleCompletion,
                      onChange: (val) => {
                        setVisualizationOption(
                          val ? VisualizationOption.Completion : null
                        );
                        mapDispatch({
                          payload: {
                            bubbleVisLegend: {
                              visible: false,
                              title: "",
                              minValue: 0,
                              maxValue: 0,
                              colors: []
                            }
                          }
                        });
                      },
                      size: "large"
                    }}
                    label={{
                      value: "Show Completion",
                      isBold: true
                    }}
                  />
                </MapVisOptionItem>

                {visualizationOption === VisualizationOption.Completion && (
                  <>
                    <div className="separator" />
                    <div className="ipdb-option-item scale-container">
                      <div className="flex-column" style={{ gap: "3px" }}>
                        <span>Scale by</span>
                        <BaseDropdown
                          value={scaleByProperty}
                          options={scaleByOptions}
                          labelKey="value"
                          onChange={setScaleByProperty}
                        />
                      </div>
                      <div className="flex-column" style={{ gap: "3px" }}>
                        <span>Value ({getUnitLabel(scaleByProperty.unit)})</span>
                        <BaseInput
                          value={displayedScaleByValue}
                          focus={() => setIsEditingScaleByValue(true)}
                          onChange={(val) => {
                            setDisplayedScaleByValue(val);
                          }}
                          blur={() => {
                            setIsEditingScaleByValue(false);
                            const numericValue = parseFloat(displayedScaleByValue);
                            if (!isNaN(numericValue) && isUnitCoreReady) {
                              const roundedValue = Math.round(numericValue * 100) / 100;
                              setDisplayedScaleByValue(roundedValue.toString());
                              const targetUnit = getTargetUnit(
                                scaleByProperty.unit,
                                unitType
                              );
                              const siValue = convertByUnit(
                                targetUnit,
                                UnitSystem.SI,
                                roundedValue,
                                2
                              );
                              setScaleByValue(siValue);
                            }
                          }}
                          type="number"
                        />
                      </div>
                      <div className="flex-column" style={{ gap: "3px" }}>
                        <span>Length ({displayLabel})</span>
                        <BaseInput
                          style={{ width: "80px" }}
                          value={displayedCompletionLength}
                          focus={() => setIsEditingCompletionLength(true)}
                          onChange={(val) => {
                            setDisplayedCompletionLength(val);
                          }}
                          blur={() => {
                            setIsEditingCompletionLength(false);
                            const numericValue = parseFloat(displayedCompletionLength);
                            if (!isNaN(numericValue)) {
                              const roundedValue = Math.round(numericValue * 100) / 100;
                              setDisplayedCompletionLength(roundedValue.toString());
                              const metersValue = displayToMeters(roundedValue);
                              setCompletionLengthValue(metersValue);
                            }
                          }}
                          type="number"
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {hasWellCoverageVisual && (
              <>
                <div className="separator" />
                <MapVisOptionItem className="ipdb-option-item">
                  <LabeledSwitch
                    testId="map-vis_show-coverage"
                    switch={{
                      showSwitchTooltip: !canToggleWellCoverage,
                      isDisabled: !canToggleWellCoverage,
                      isChecked: coverageOptions.showCoverage && canToggleWellCoverage,
                      onChange: (val) => {
                        setVisualizationOption(val ? VisualizationOption.Coverage : null);
                        setCoverageOptions({ ...coverageOptions, showCoverage: val });
                        mapDispatch({
                          payload: {
                            bubbleVisLegend: {
                              visible: false,
                              title: "",
                              minValue: 0,
                              maxValue: 0,
                              colors: []
                            }
                          }
                        });
                      },
                      size: "large"
                    }}
                    label={{
                      value: "Show Well Coverage",
                      isBold: true
                    }}
                  />
                </MapVisOptionItem>

                {coverageOptions.showCoverage && (
                  <>
                    <div className="separator" />
                    <div className="ipdb-option-item scale-container">
                      <div className="flex-column" style={{ gap: "3px" }}>
                        <span>Radius ({displayLabel})</span>
                        <Input
                          type={"number"}
                          step={1}
                          min={1}
                          max={10000}
                          value={displayedCoverageRadius}
                          onFocus={() => setIsEditingCoverageRadius(true)}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            setDisplayedCoverageRadius(inputValue);
                          }}
                          onBlur={() => {
                            setIsEditingCoverageRadius(false);
                            const numericValue = parseFloat(displayedCoverageRadius);
                            if (!isNaN(numericValue)) {
                              const roundedValue = Math.round(numericValue * 100) / 100;
                              setDisplayedCoverageRadius(roundedValue.toString());
                              const metersValue = displayToMeters(roundedValue);
                              setCoverageOptions({
                                ...coverageOptions,
                                radius: metersValue
                              });
                            }
                          }}
                        />
                      </div>
                      <div className="flex-column" style={{ gap: "3px" }}>
                        <span>Coverage Shape</span>
                        <Select
                          value={coverageOptions.coverageType}
                          onChange={(value) => {
                            setCoverageOptions({
                              ...coverageOptions,
                              coverageType: value
                            });
                          }}
                          options={[
                            { value: "Bubble", label: "Bubble" },
                            { value: "WellOutline", label: "Well Outline" }
                          ]}
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {hasXdaOverlapVisual && (
              <>
                <div className="separator" />
                <MapVisOptionItem className="ipdb-option-item">
                  <LabeledSwitch
                    testId="map-vis_show-xda-projection"
                    switch={{
                      showSwitchTooltip: !canToggleXdaProjection,
                      switchTooltipText: xdaProjectionTooltip,
                      isDisabled: !canToggleXdaProjection,
                      isChecked:
                        visualizationOption === VisualizationOption.XdaProjection &&
                        canToggleXdaProjection,
                      onChange: (val) => {
                        setVisualizationOption(
                          val ? VisualizationOption.XdaProjection : null
                        );
                      },
                      size: "large"
                    }}
                    label={{
                      value: "Show XDA Overlap",
                      isBold: true
                    }}
                  />
                </MapVisOptionItem>

                {visualizationOption === VisualizationOption.XdaProjection && (
                  <XdaProjectionOptions />
                )}
              </>
            )}
          </div>
        }
        onOpenChange={(v) => {
          setIsPopoverVisible(v);
          if (!v) {
            setShowBubbleExtrudeFields(false);
          }
        }}>
        <ToolbarButton
          icon={<Bubble data-testid="map-vis-options" size={20} />}
          isMenuButton={true}
          styleKey={"mapToolbar"}
          overflowLabel="Visualization Options"
          tooltipText="Visualization Options"
          active={
            (visualizationOption === VisualizationOption.Bubble && canShowBubble) ||
            (visualizationOption === VisualizationOption.Completion &&
              canToggleCompletion) ||
            (visualizationOption === VisualizationOption.Coverage &&
              canToggleWellCoverage) ||
            (visualizationOption === VisualizationOption.XdaProjection &&
              canToggleXdaProjection)
          }
        />
      </Popover>
    </>
  );
}

/**
 * Renderless component that mounts `useXdaProjectionLayer` only while
 * XDA projection is active. Unmounting tears down the map layer and
 * cancels the react-query subscription so no fetches fire when the
 * feature is off.
 */
function XdaProjectionLayerEffect(props: XdaProjectionLayerProps): null {
  useXdaProjectionLayer(props);
  return null;
}

export const MapVisOptionItem = styled.div`
  padding: 5px 20px;

  .ant-slider {
    min-width: 140px;
  }

  .ant-select {
    min-width: 150px;
  }

  label {
    font-weight: var(--fontWeightMedium);
    color: #9b9b9b;
  }

  .ant-input {
    max-width: 150px;
  }

  label.active {
    color: var(--color-text);
    font-weight: var(--fontWeightBold);
  }

  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  justify-content: space-between;

  .removable-property {
    height: 32px;
    background-color: #d9e1e266;
    padding: 5px 8px;
    border-radius: 4px;
    min-width: 140px;
    position: relative;
    justify-content: space-between;

    .icon-toggle {
      svg {
        color: #b5b5b5;
        transition: color var(--duration);
      }

      &:hover {
        svg {
          color: var(--color-danger);
        }
      }
    }
  }
`;

const emptyFeatureCollection = {
  type: "FeatureCollection",
  features: []
} as GeoJSON.FeatureCollection;
